import { z } from 'zod';

import { VariantActions } from '../lib/types/Loader';
import { PartialOptionsSchema as PartialBumperOptionsSchema } from '../lib/types/WidgetVariantBumper';
import { PartialOptionsSchema as PartialFabOptionsSchema } from '../lib/types/WidgetVariantFab';
import { PartialOptionsSchema as PartialFacOptionsSchema } from '../lib/types/WidgetVariantFac';
import { PartialOptionsSchema as PartialEmbeddedOptionsSchema } from '../lib/types/WidgetVariantEmbedded';
import { getLoaderNamespace, getLoaderReadyEventName } from '../lib/utils';
import { createTemporaryActionQueue } from './createTemporaryActionQueue';
import { getWidgetClient } from './getWidgetClient';
import { Variant } from './Variant';
import { LoaderScriptDataAttributes } from './LoaderScriptDataAttributes';

export const VariantOptionOverridesSchema = PartialBumperOptionsSchema.merge(PartialFacOptionsSchema)
  .merge(PartialEmbeddedOptionsSchema)
  .merge(PartialFabOptionsSchema);

export type VariantOptionOverrides = z.infer<typeof VariantOptionOverridesSchema>;

export const LoadWidgetPropsSchema = z.object({
  flowSystemId: z.string().optional(),
  id: z.string(),
  loaderUrl: z.string().optional(),
  namespace: z.string().optional(),
  showStateDebugger: z.boolean().optional(),
  variant: z.nativeEnum(Variant).optional(),
  variantOptionOverrides: VariantOptionOverridesSchema.optional(),
  context: z.record(z.unknown()).optional(),
  trackingParameters: z.record(z.string()).optional(),
  parentElementId: z.string().optional(),
  language: z.string().optional(),
});

export type LoadWidgetProps<V extends Variant = Variant> = z.infer<typeof LoadWidgetPropsSchema> & {
  variant?: V;
};

/**
 * Loads a widget script and initializes the widget with the given properties.
 *
 * @template V - The variant type of the widget.
 * @param {Object} props - The properties to initialize the widget with.
 * @param {string} props.id - The widget ID.
 * @param {V} props.variant - The variant of the widget.
 * @param {string} [props.flowSystemId] - The flow system ID. (optional)
 * @param {string} [props.loaderUrl='https://asurion-widget.prd.aws.service-initiation.asurion.com/widget-loader.js'] - The URL of the loader script. (optional)
 * @param {string} [props.namespace='_si_widget'] - The namespace for the loader script that will be created on the window object. (optional)
 * @param {boolean} [props.showStateDebugger] - Whether to show the state debugger. (optional)
 * @param {VariantOptionOverrides} [props.variantOptionOverrides] - Instance specific overrides for the variant options. (optional)
 * @param {Record<string, unknown>} [props.context] - Additional context for the widget. (optional)
 * @param {Record<string, string>} [props.trackingParameters] - Additional tracking params for the widget. Will be appended to external links within the widget (optional)
 * @returns {Promise<VariantActions[V]>} - A promise that resolves to the actions of the widget variant.
 */
export const loadWidget = async <V extends Variant = Variant.FLOATING_ACTION_CARD>({
  loaderUrl = 'https://asurion-widget.prd.aws.service-initiation.asurion.com/widget-loader.js',
  namespace = '_si_widget',
  variant = Variant.FLOATING_ACTION_CARD as V,
  ...props
}: LoadWidgetProps<V>): Promise<VariantActions[V]> => {
  createTemporaryActionQueue(namespace);

  const language = props.language || document.documentElement.lang || navigator.language || 'en';
  const loaderScriptElement = document.getElementById(namespace) as HTMLScriptElement | null;

  if (!loaderScriptElement) {
    const script = document.createElement('script');
    script.id = namespace;
    script.src = loaderUrl;
    script.setAttribute(LoaderScriptDataAttributes.AUTO_LOAD_DISABLED, '');

    const loaderReady = new Promise<void>((resolve) =>
      script.addEventListener(getLoaderReadyEventName(namespace), () => resolve())
    );

    document.body.appendChild(script);

    await loaderReady;
  }

  await (window as unknown as Record<string, (props: LoadWidgetProps<V>) => Promise<void>>)[
    getLoaderNamespace(namespace)
  ]({
    ...props,
    loaderUrl,
    namespace,
    variant,
    language,
  });

  return getWidgetClient({ namespace, variant }) as VariantActions[V];
};
